import React from 'react';
import {useAppDispatch} from '../hooks';
import {registerUser} from '../features/user/userSlice';
import {useNavigate} from 'react-router-dom';
import {Button, Card, Checkbox, Col, Form, Input, Layout, message, Row, Typography} from 'antd';
import {LockOutlined, MailOutlined, UserOutlined} from '@ant-design/icons';
import {register} from '../api/auth';


const {Title, Text} = Typography;
const {Content} = Layout;

interface RegisterProps {
    onClose?: () => void;
}

const Register: React.FC<RegisterProps> = ({onClose}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const onFinish = async (values: any) => {
        if (!values.agreement) {
            message.error("Vous devez accepter les conditions.");
            return;
        }

        if (values.password !== values.confirmPassword) {
            message.error("Les mots de passe ne correspondent pas.");
            return;
        }

        try {
            dispatch(registerUser({user: values.user, firm: values.firm, token: values.token}));

            await register({
                firstName: values.firstName,
                lastName: values.lastName,
                username: values.username,
                email: values.email,
                firmName: values.firmName,
                password: values.password,
            });

            if (onClose) onClose();
            navigate('/');
        } catch (error: any) {
            message.error(error.message || 'Registration failed.');
        }
    };

    return (
        <Layout style={{minHeight: '50vh'}}>
            <Content>
                <Row justify="center" align="middle" style={{minHeight: '50vh'}}>
                    <Col xs={20} sm={20} md={20} lg={20} xl={20}>
                        <Card style={{borderRadius: 10}}>
                            <Title level={2} style={{textAlign: 'center', marginBottom: 10}}>
                                Create Account
                            </Title>
                            <Form
                                name="register"
                                layout="vertical"
                                onFinish={onFinish}
                                initialValues={{agreement: false}}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="firstName"
                                            label="First Name"
                                            rules={[{required: true, message: 'Please enter your first name!'}]}
                                        >
                                            <Input placeholder="First Name"/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="lastName"
                                            label="Last Name"
                                            rules={[{required: true, message: 'Please enter your last name!'}]}
                                        >
                                            <Input placeholder="Last Name"/>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="username"
                                            label="Username"
                                            rules={[{required: true, message: 'Please enter your username!'}]}
                                        >
                                            <Input prefix={<UserOutlined/>} placeholder="Username"/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="email"
                                            label="Email"
                                            rules={[
                                                {required: true, message: 'Please enter your email!'},
                                                {type: 'email', message: 'The input is not a valid email!'},
                                            ]}
                                        >
                                            <Input prefix={<MailOutlined/>} placeholder="Email"/>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="firmName"
                                    label="Firm"
                                    rules={[{required: true, message: 'Please enter your firm name!'}]}
                                >
                                    <Input placeholder="Firm"/>
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="password"
                                            label="Password"
                                            rules={[{required: true, message: 'Please enter your password!'}]}
                                        >
                                            <Input.Password prefix={<LockOutlined/>} placeholder="Password"/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="confirmPassword"
                                            label="Confirm Password"
                                            dependencies={['password']}
                                            rules={[
                                                {required: true, message: 'Please confirm your password!'},
                                                ({getFieldValue}) => ({
                                                    validator(_, value) {
                                                        if (!value || getFieldValue('password') === value) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject('Passwords do not match!');
                                                    },
                                                }),
                                            ]}
                                        >
                                            <Input.Password prefix={<LockOutlined/>} placeholder="Confirm Password"/>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item name="agreement" valuePropName="checked"
                                           rules={[{validator: (_, value) => value ? Promise.resolve() : Promise.reject('You must accept the agreement')}]}>
                                    <Checkbox>I accept the terms of the agreement</Checkbox>
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" block>
                                        Sign Up
                                    </Button>
                                </Form.Item>

                                <Form.Item style={{textAlign: 'center'}}>
                                    <Text>Already have an account? </Text>
                                    <Button type="link" onClick={() => navigate('/login')}>Log in</Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default Register;
